import Papa from "papaparse";

import { cellText } from "@/core/lib/csv";
import type { ClassificationRowData } from "@/openapi";

interface BuildTemplateParams {
  rows: ClassificationRowData[];
  identifierColumns: string[];
  classificationColumns: string[];
}

/**
 * The starting file for an import: every row that exists, identified, with the
 * classification columns left blank for the auditor to fill in. Blank cells are
 * read as "no opinion", so a partly filled file only touches the rows it names.
 *
 * Semicolon-separated with a byte-order mark, which is what Excel in a Dutch
 * locale opens as a spreadsheet; the importer guesses the delimiter either way.
 *
 * Identifier columns are quoted, because account numbers like 0570 are text
 * whose leading zero is part of the identity. Quoting states that in the file
 * for every reader that honours it: note that Excel is not one of them when a
 * .csv is opened by double-clicking.
 */
export function buildCsvTemplate({
  identifierColumns,
  valueColumns,
  identifierRows,
}: {
  identifierColumns: string[];
  valueColumns: ReadonlyArray<string>;
  identifierRows: ReadonlyArray<ReadonlyArray<string>>;
}): string {
  const header = [...identifierColumns, ...valueColumns];

  const lines = identifierRows.map((identifiers) => [
    ...identifiers,
    ...valueColumns.map(() => ""),
  ]);

  return Papa.unparse([header, ...lines], {
    delimiter: ";",
    quotes: header.map((_, index) => index < identifierColumns.length),
  });
}

export function buildClassificationCsvTemplate({
  rows,
  identifierColumns,
  classificationColumns,
}: BuildTemplateParams): string {
  return buildCsvTemplate({
    identifierColumns,
    valueColumns: classificationColumns,
    identifierRows: rows.map((row) =>
      identifierColumns.map((column) => cellText(row[column])),
    ),
  });
}

export const CSV_TEMPLATE_BOM = "\uFEFF";
